import { useNavigate } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Eye, EyeOff } from "lucide-react"; 
import Header from "../Components/Header";
import Card from "../Components/Card";
import BlueButton from "../Components/BlueButton";
import RedButton from "../Components/RedButton";
import { BRAND } from "../css/brand";
import GoBackButton from "../Components/GoBackButton";

export default function Account({ brand }) {
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    sessionStorage.removeItem("authUserData1");
    navigate("/login");
  };

  // 🔹 Get user data from session
  const authUserString = sessionStorage.getItem("authUserData1");
  const authUser = JSON.parse(authUserString);
  const user = authUser?.data[0] || {};

  const UserName = user.business_name;
  const UserCode = user.org_code;
  const UserLocation = user.state;
  const UserPhone = user.mobile;
  const UserMail = user.email;
  const UserGST = user.gst;
  const UserPassword = user.password;
  const UserOwnerName = user.owner_name;
  const UserPan = user.pan;
  const UserPin = user.pincode;
  const userId = user.id; // ✅ assuming API path /org_user/:id

  // 🔹 State for password change inputs
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // ✅ Handle Password Update
  const handleUpdatePassword = async () => {
    if (!newPassword || !confirmPassword) {
      Swal.fire("Missing Fields", "Please enter all password fields.", "warning");
      return;
    }
    if (newPassword !== confirmPassword) {
      Swal.fire("Mismatch", "New and Confirm passwords do not match.", "error");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        password: newPassword,
        mobile: UserPhone,
        email: UserMail,
        business_name: UserName,
        gst: UserGST,
        owner_name: UserOwnerName,
        pan: UserPan,
        pincode: UserPin,
      };

      const res = await axios.post(`https://spillas.com/api/v1/org_user/${userId}`, payload);

      if (res.status === 200) {
        Swal.fire("Success", "Password updated successfully!", "success");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        Swal.fire("Error", "Failed to update password.", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Something went wrong while updating password.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      <GoBackButton/>
      <Header brand={BRAND} title="Account & Profile" />

      {/* Profile Section */}
      <Card brand={BRAND} title="Profile">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <input
            placeholder="Vendor Code"
            className="rounded-lg border px-3 py-2 text-sm"
            value={UserCode || "-"}
            readOnly
          />
          <input
            placeholder="User Name"
            className="rounded-lg border px-3 py-2 text-sm"
            value={UserName || "-"}
            readOnly
          />
          <input
            placeholder="Region"
            className="rounded-lg border px-3 py-2 text-sm"
            value={UserLocation || "-"}
            readOnly
          />
          <input
            placeholder="+91 90000 00000"
            className="rounded-lg border px-3 py-2 text-sm"
            value={UserPhone || "-"}
            readOnly
          />
          <input
            placeholder="user@example.com"
            className="rounded-lg border px-3 py-2 text-sm"
            value={UserMail || "-"}
            readOnly
          />
          <input
            placeholder="GSTIN/Other"
            className="rounded-lg border px-3 py-2 text-sm"
            value={UserGST || "-"}
            readOnly
          />
        </div>
      </Card>

      {/* Password Section */}

      <Card brand={BRAND} title="Change Password">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {/* Current Password */}
          <div className="relative">
            <input
              type={showCurrent ? "text" : "password"}
              placeholder="Current Password"
              className="w-full rounded-lg border px-3 py-2 text-sm pr-10"
              value={UserPassword || "-"}
              readOnly
            />
            <button
              type="button"
              onClick={() => setShowCurrent(!showCurrent)}
              className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700"
            >
              {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {/* New Password */}
          <div className="relative">
            <input
              type={showNew ? "text" : "password"}
              placeholder="New Password"
              className="w-full rounded-lg border px-3 py-2 text-sm pr-10"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowNew(!showNew)}
              className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700"
            >
              {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {/* Confirm Password */}
          <div className="relative">
            <input
              type={showConfirm ? "text" : "password"}
              placeholder="Confirm New Password"
              className="w-full rounded-lg border px-3 py-2 text-sm pr-10"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700"
            >
              {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>


        </div>
        <div className="flex justify-center sm:justify-end w-full">
          <BlueButton
            brand={BRAND}
            onClick={handleUpdatePassword}
            disabled={loading}
            className="w-auto px-6"
          >
            {loading ? "Updating..." : "Update Password"}
          </BlueButton>
        </div>

      </Card>


      {/* Logout Section */}
      <div className="flex items-center justify-between rounded-2xl border bg-white p-4 shadow-sm">
        <div>
          <p className="text-sm font-semibold" style={{ color: "#0A3E91" }}>
            Logout Section
          </p>
          <p className="text-xs text-slate-500">
            Manage your Account Or logout.
          </p>
        </div>
        <RedButton brand={BRAND} onClick={handleLogout}>
          Logout
        </RedButton>
      </div>
    </div>
  );
}


// import { useNavigate } from "react-router-dom";

// import Header from "../Components/Header";
// import Card from "../Components/Card";
// import BlueButton from "../Components/BlueButton";
// import { BRAND } from "../css/brand";
// import RedButton from "../Components/RedButton";

// export default function Account( {brand}) {


//   const navigate = useNavigate();
//   const handleLogout = () => {
//     sessionStorage.removeItem("authUserData1");
//     navigate("/login");
//   };

//   const authUserString = sessionStorage.getItem("authUserData1");
//   const authUser = JSON.parse(authUserString);
//   const UserOwnerName = authUser?.data[0]?.owner_name;
//   const UserName = authUser?.data[0]?.business_name;
//   const UserCode = authUser?.data[0]?.org_code;
//   const UserLocation = authUser?.data[0]?.state;
//   const UserPhone = authUser?.data[0]?.mobile;
//   const UserMail = authUser?.data[0]?.email;
//   const UserGST = authUser?.data[0]?.gst;
//   const UserPassword = authUser?.data[0]?.password;
//   const UserPan = authUser?.data[0]?.pan;
//   const UserPin = authUser?.data[0]?.pincode;

//   return (
//     <div className="space-y-3 sm:space-y-4">
//       <Header brand={BRAND} title="Account & Profile" />
//       <Card brand={BRAND} title="Profile">
//         <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
//           <input placeholder="Vendor Code" className="rounded-lg border px-3 py-2 text-sm" value={UserCode || "-"}/>
//           <input placeholder="User Name" className="rounded-lg border px-3 py-2 text-sm" value={UserName || "-"} />
          
//           <input placeholder="Region" className="rounded-lg border px-3 py-2 text-sm" value={UserLocation || "-"} />
//           <input placeholder="+91 90000 00000" className="rounded-lg border px-3 py-2 text-sm" value={UserPhone || "-"} />
//           <input placeholder="user@example.com" className="rounded-lg border px-3 py-2 text-sm" value={UserMail || "-"} />
//           <input placeholder="GSTIN/Other" className="rounded-lg border px-3 py-2 text-sm" value={UserGST || "-"} />
//           {/* <div className="sm:col-span-3 flex justify-end"><BlueButton brand={BRAND}>Update Profile</BlueButton></div> */}
//         </div>
//       </Card>
//       <Card brand={BRAND} title="Change Password">
//         <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
//           <input type="password" placeholder="Current Password" className="rounded-lg border px-3 py-2 text-sm" value={UserPassword || "-"} />
//           <input type="password" placeholder="New Password" className="rounded-lg border px-3 py-2 text-sm" />
//           <input type="password" placeholder="Confirm New Password" className="rounded-lg border px-3 py-2 text-sm" />
//           <BlueButton brand={BRAND} className="sm:col-span-3">Update Password</BlueButton>
//         </div>
//       </Card>

//       <div className="flex items-center justify-between rounded-2xl border bg-white p-4 shadow-sm">
//         <div>
//           <p className="text-sm font-semibold" style={{ color: "#0A3E91" }}>
//             Session
//           </p>
//           <p className="text-xs text-slate-500">
//             Manage your active session and logout.
//           </p>
//         </div>
//         <RedButton brand={BRAND} onClick={handleLogout}>Logout</RedButton>
//       </div>

//     </div>
//   );
// }